import { useAuth } from "@/App";

/**
 * What the signed-in user is allowed to do (round 10).
 *
 * The list comes from GET /auth/me, resolved server-side from the same matrix
 * require_permission applies. One source, deliberately: a UI that decided for itself
 * what a role may do would be a second permission system to keep in step with the
 * first, and the two would disagree the first time somebody edited the matrix in the
 * admin console.
 *
 * This hides controls. It does not secure anything: every one of these permissions is
 * enforced on the route as well, and the API is the thing that actually says no. What
 * this prevents is a merchant clicking a button that was never going to work.
 */

// Fail CLOSED while /auth/me is still in flight. An empty list briefly hides a control
// somebody is allowed to use; the alternative shows a control they are not, and they
// click it. A flash of absence is the cheaper mistake.
const NONE = [];

export function usePermissions() {
  const { user } = useAuth();
  const granted = user?.permissions || NONE;

  return {
    /** Whether the user holds this permission. */
    can: (permission) => granted.includes(permission),
    /** Whether they hold every one of these. */
    canAll: (...permissions) => permissions.every((p) => granted.includes(p)),
    /** Whether they hold at least one. */
    canAny: (...permissions) => permissions.some((p) => granted.includes(p)),
    /** The whole list, for the rare caller that needs to reason over it. */
    permissions: granted,
    /**
     * What the backend judged them as. Not always `advertiser_role`: every user
     * predating the team feature resolves through the legacy mapping. Used to explain
     * WHY something is missing rather than only that it is.
     */
    role: user?.effective_role || null,
  };
}

/**
 * A short sentence for a control a role cannot use.
 *
 * Written once so every disabled tooltip in the product says the same thing. "Your
 * role does not allow this" alone invites a support ticket; naming the role tells the
 * person what to ask their account owner for.
 */
export function deniedReason(role) {
  const named = {
    MARKETING: "Marketing",
    FINANCE: "Finance",
    READONLY_FINANCE: "Read Only (Finance)",
    READONLY: "Read Only",
  }[role];
  return named
    ? `Your ${named} role does not allow this. Ask your account owner.`
    : "Your role does not allow this. Ask your account owner.";
}
